// Stores
import { useUiStore } from '@stores/ui';

// Personal libs
import { Repository } from './common/repository.class';
import { ApiError } from '@shared/error/api.error';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { $t } from '@i18n/utils';
import {
  IApiPostSignup,
  IApiPostSignupAccept,
  IApiPostSignupReject,
  apiPostSignup,
  apiPostSignupAccept,
  apiPostSignupReject,
} from '@data/datasource/remote/signup.api';
import { timestampToLitteral } from '@shared/lib/date.lib';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

class SignupRepository extends Repository {
  constructor() {
    super();
  }

  /*****************************************
   *
   * POST FUNCTIONS
   *
   *****************************************/
  // Signup processus
  signup(
    params: IApiPostSignup,
  ): Promise<void | IApiResponse<string, undefined>> {
    return apiPostSignup(params).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (['SIGNUP_ERR_07'].includes(err.code ?? '')) {
          throw new Error(
            $t('signup.msg.technicalError', {
              errorCode: err.code,
              rateLimitRemaining: err.rateLimit?.remaining,
              rateLimitReset: timestampToLitteral(err.rateLimit?.reset),
            }),
          );
        } else if (
          [
            'SIGNUP_ERR_01',
            'SIGNUP_ERR_02',
            'SIGNUP_ERR_03',
            'SIGNUP_ERR_05',
          ].includes(err.code ?? '')
        ) {
          throw new Error(
            $t('signup.msg.invalidProvidedData', {
              errorCode: 'SIGNUP_ERR_01',
              rateLimitRemaining: err.rateLimit?.remaining,
              rateLimitReset: timestampToLitteral(err.rateLimit?.reset),
            }),
          );
        } else {
          throw new Error($t(`signup.msg.${err.code}`));
        }
      } else {
        if (sessionManager.isOnline.value) {
          throw new Error($t('generic.unknownError'));
        } else {
          throw new Error($t('generic.healthCheck.backendNotReachable'));
        }
      }
    });
  }

  // Signup processus - accept
  signupAccept(
    params: IApiPostSignupAccept,
  ): Promise<void | IApiResponse<string, undefined>> {
    const uiStore = useUiStore();

    return apiPostSignupAccept(params).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code == 'SIGNUP_ACCEPT_ERR_01') {
          throw new Error('Utilisateur inconnu');
        } else if (err.code == 'SIGNUP_ACCEPT_ERR_02') {
          throw new Error('Utilisareur déjà inscrit');
        } else if (err.code == 'SIGNUP_ACCEPT_ERR_03') {
          throw new Error(
            "Vous n'avez pas le droit de valider la demande d'inscription d'un utilisateur.",
          );
        }
      } else {
        if (sessionManager.isOnline.value) {
          throw new Error($t('generic.unknownError'));
        } else {
          throw new Error($t('generic.healthCheck.backendNotReachable'));
        }
      }
    });
  }

  // Signup processus - reject
  signupReject(
    params: IApiPostSignupReject,
  ): Promise<void | IApiResponse<string, undefined>> {
    return apiPostSignupReject(params).catch((err: ErrorConstructor) => {
      if (err instanceof ApiError) {
        if (err.code == 'SIGNUP_REJECT_ERR_01') {
          throw new Error('Utilisateur inconnu');
        } else if (err.code == 'SIGNUP_REJECT_ERR_02') {
          throw new Error('Utilisareur déjà inscrit');
        } else if (err.code == 'SIGNUP_REJECT_ERR_03') {
          throw new Error(
            "Vous n'avez pas le droit de rejeter la demande d'inscription d'un utilisateur.",
          );
        } else if (err.code == 'SIGNUP_REJECT_ERR_04') {
          throw new Error("Erreur technique lors de l'envoi de mail.");
        } else if (err.code == 'SIGNUP_REJECT_ERR_05') {
          throw new Error(
            "Erreur technique lors de la suppression de l'utilisateur temporaire.",
          );
        }
      } else {
        const uiStore = useUiStore();
        if (sessionManager.isOnline.value) {
          throw new Error($t('generic.unknownError'));
        } else {
          throw new Error($t('generic.healthCheck.backendNotReachable'));
        }
      }
    });
  }
}

export default new SignupRepository();
